import type { FieldSelection, JavascriptSerializable } from '#layers/data-model/public';
import type { CollectionCodeSnippetOptions } from './types';
import { hasCompositePrimaryKey, toJavascriptSerializable } from '#layers/data-model/public';
import { getDefaultCodeFilter, getDefaultCodeKey } from './defaults';
import { normalizeCodeSelection, resolveCodeSelection, toSerializableFieldSelection } from './fields';
import { DELETE_OPERATIONS } from './operation';
import { getCollectionWriteDataItem } from './write-data';

/**
 * Builds the object literal payload passed to SDK read methods.
 */
export function createSdkReadPayload(options: CollectionCodeSnippetOptions): { [key: string]: JavascriptSerializable } {
	const payload: { [key: string]: JavascriptSerializable } = {};

	if (options.operation === 'readOne') {
		payload.key = options.key ?? getDefaultCodeKey(options.collection);
	}

	Object.assign(payload, toSerializableFieldSelection(resolveCodeSelection(options)));

	addDefined(payload, 'filter', options.filter);

	if (options.operation !== 'readOne') {
		addDefined(payload, 'sort', options.sort);
	}

	if (options.operation === 'readMany') {
		addDefined(payload, 'limit', options.limit);
		addDefined(payload, 'offset', options.offset);
	}

	return payload;
}

/**
 * Builds the object literal payload passed to SDK create, update, and delete methods.
 */
export function createSdkWritePayload(options: CollectionCodeSnippetOptions): { [key: string]: JavascriptSerializable } {
	const payload: { [key: string]: JavascriptSerializable } = {};

	if (options.operation === 'updateOne' || options.operation === 'deleteOne') {
		payload.key = options.key ?? getDefaultCodeKey(options.collection);
	}

	if (options.operation === 'createOne' || options.operation === 'createMany' || options.operation === 'updateOne' || options.operation === 'updateMany') {
		payload.data = resolveWriteData(options);
	}

	// Bulk writes need a filter. Use a schema-derived example instead of emitting an unconstrained operation.
	if (options.operation === 'updateMany' || options.operation === 'deleteMany') {
		payload.filter = toJavascriptSerializable(options.filter ?? getDefaultCodeFilter(options.collection));
	}
	else {
		addDefined(payload, 'filter', options.filter);
	}

	const selection = resolveReturnSelection(options);

	if (selection) {
		Object.assign(payload, toSerializableFieldSelection(selection));
	}

	if (options.operation === 'updateMany') {
		addDefined(payload, 'sort', options.sort);
		addDefined(payload, 'limit', options.limit);
	}

	return payload;
}

/**
 * Adds a property to a generated payload only when the caller provided a value.
 */
export function addDefined(target: { [key: string]: JavascriptSerializable }, key: string, value: unknown): void {
	if (value !== undefined) {
		target[key] = toJavascriptSerializable(value);
	}
}

/**
 * Resolves return fields for operations that may include returned records.
 */
export function resolveReturnSelection(options: CollectionCodeSnippetOptions): FieldSelection | undefined {
	if (DELETE_OPERATIONS.has(options.operation)) {
		return normalizeCodeSelection(options.selection);
	}

	return resolveCodeSelection(options);
}

/**
 * Checks whether the operation should assign a returned value in SDK snippets.
 */
export function shouldReturnFields(options: CollectionCodeSnippetOptions): boolean {
	return Boolean(resolveReturnSelection(options));
}

/**
 * Resolves explicit write data or derives a sample payload from collection metadata.
 */
export function resolveWriteData(options: CollectionCodeSnippetOptions): JavascriptSerializable {
	if (options.data !== undefined) {
		return toJavascriptSerializable(options.data);
	}

	const data = getCollectionWriteDataItem(options.collection, undefined, {
		includePrimaryKeyFields: shouldIncludePrimaryKeyFieldsInWriteData(options),
	});

	return options.operation === 'createMany' ? [data] : data;
}

/**
 * Checks whether create payload samples must include caller-provided primary key fields.
 */
function shouldIncludePrimaryKeyFieldsInWriteData(options: CollectionCodeSnippetOptions) {
	return (options.operation === 'createOne' || options.operation === 'createMany')
		&& hasCompositePrimaryKey(options.collection);
}
